import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { api } from '@/api';
import { User, Bell, Mail, Clock, Save, Send, Eye, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const DIGEST_SECTIONS = [
  { key: 'overdue_tasks', label: 'Overdue Tasks', desc: 'Tasks past their due date' },
  { key: 'tasks_due_today', label: 'Tasks Due Today', desc: 'Tasks due within the day' },
  { key: 'tasks_due_this_week', label: 'Tasks Due This Week', desc: 'Tasks due in the next 7 days' },
  { key: 'high_priority_tasks', label: 'High Priority Tasks', desc: 'Urgent and high priority open tasks' },
  { key: 'new_meeting_minute_tasks', label: 'New Meeting Minute Tasks', desc: 'Tasks extracted from meeting minutes in the last 24h' },
  { key: 'tasks_assigned_last_24h', label: 'Recently Assigned Tasks', desc: 'Tasks assigned in the last 24 hours' },
  { key: 'tasks_updated_last_24h', label: 'Recently Updated Tasks', desc: 'Tasks updated in the last 24 hours' },
  { key: 'tasks_completed_yesterday', label: 'Completed Yesterday', desc: 'Tasks completed the previous day' },
  { key: 'shop_drawings_outstanding', label: 'Shop Drawings Outstanding', desc: 'Pending shop drawing packages' },
  { key: 'rfi_due_overdue', label: 'RFIs Due / Overdue', desc: 'RFIs approaching or past due date' },
  { key: 'irs_items_due', label: 'Information Required (IRS)', desc: 'IRS items due this week' },
  { key: 'procurement_items_due', label: 'Procurement Items Due', desc: 'Procurement deliveries due this week' },
  { key: 'notice_claim_deadlines', label: 'Notice & Claim Deadlines', desc: 'Contract notice and claim response deadlines' },
  { key: 'programme_submissions', label: 'Programme Submissions', desc: 'Programme submission deadlines' },
  { key: 'si_response_deadlines', label: 'SI Response Deadlines', desc: 'Site instruction response deadlines' },
];

export default function UserSettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showDigestContent, setShowDigestContent] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const [profile, setProfile] = useState({
    full_name: '',
    phone: '',
    company: '',
    job_title: '',
  });

  const [notifications, setNotifications] = useState({
    daily_digest_enabled: true,
    daily_digest_time: '06:00',
    send_empty_digest: false,
    instant_task_assigned: true,
    instant_overdue_alert: true,
    instant_module_deadlines: false,
    digest_preferences: {},
  });

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.userSettings.getProfile();
      const data = res.data;
      setProfile({
        full_name: data.full_name || data.name || '',
        phone: data.phone || '',
        company: data.company || '',
        job_title: data.job_title || '',
      });
      if (data.notification_settings) {
        setNotifications(data.notification_settings);
      }
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await api.userSettings.updateProfile(profile);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      setSaving(true);
      await api.userSettings.updateNotifications(notifications);
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestDigest = async () => {
    try {
      setSendingTest(true);
      const res = await api.userSettings.sendTestDigest();
      const d = res.data;
      if (d.sent) {
        toast.success(`Test digest sent to ${user?.email} with ${d.items_count} items`);
      } else {
        toast.info(d.message || 'Digest sent (no actionable items found)');
      }
    } catch (err) {
      toast.error('Failed to send test digest');
    } finally {
      setSendingTest(false);
    }
  };

  const handlePreviewDigest = async () => {
    try {
      setPreviewLoading(true);
      const res = await api.userSettings.previewDigest();
      setPreviewData(res.data);
      setShowDigestContent(true);
    } catch (err) {
      toast.error('Failed to preview digest');
    } finally {
      setPreviewLoading(false);
    }
  };

  const updateDigestPref = (key, value) => {
    setNotifications(prev => ({
      ...prev,
      digest_preferences: {
        ...prev.digest_preferences,
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="user-settings-page">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="settings-page-title">Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your profile and notification preferences</p>
        </div>

        {/* Profile Section */}
        <Card data-testid="profile-section">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              <CardTitle className="text-lg">Profile</CardTitle>
            </div>
            <CardDescription>Your personal details. Email and role are managed by your administrator.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wide">Email</Label>
                <Input value={user?.email || ''} disabled className="mt-1 bg-slate-50" data-testid="profile-email" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wide">Role</Label>
                <Input value={(user?.role || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} disabled className="mt-1 bg-slate-50" data-testid="profile-role" />
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name" className="text-xs text-slate-500 uppercase tracking-wide">Full Name</Label>
                <Input
                  id="full_name"
                  value={profile.full_name}
                  onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))}
                  className="mt-1"
                  data-testid="profile-full-name"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs text-slate-500 uppercase tracking-wide">Phone</Label>
                <Input
                  id="phone"
                  value={profile.phone}
                  onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                  className="mt-1"
                  data-testid="profile-phone"
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-xs text-slate-500 uppercase tracking-wide">Company</Label>
                <Input
                  id="company"
                  value={profile.company}
                  onChange={e => setProfile(p => ({ ...p, company: e.target.value }))}
                  className="mt-1"
                  data-testid="profile-company"
                />
              </div>
              <div>
                <Label htmlFor="job_title" className="text-xs text-slate-500 uppercase tracking-wide">Job Title</Label>
                <Input
                  id="job_title"
                  value={profile.job_title}
                  onChange={e => setProfile(p => ({ ...p, job_title: e.target.value }))}
                  className="mt-1"
                  data-testid="profile-job-title"
                />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Profile
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Daily Digest Section */}
        <Card data-testid="digest-section">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              <CardTitle className="text-lg">Daily Digest Email</CardTitle>
            </div>
            <CardDescription>Receive a daily summary of overdue items, upcoming deadlines, and new tasks across all modules.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border" data-testid="digest-enabled-toggle">
              <div>
                <p className="text-sm font-medium text-slate-900">Enable Daily Digest</p>
                <p className="text-xs text-slate-500">Sent daily at 06:00 AM SAST</p>
              </div>
              <Switch
                checked={notifications.daily_digest_enabled}
                onCheckedChange={v => setNotifications(n => ({ ...n, daily_digest_enabled: v }))}
                data-testid="digest-enabled-switch"
              />
            </div>

            {notifications.daily_digest_enabled && (
              <>
                {/* Send empty digest toggle */}
                <div className="flex items-center justify-between px-3" data-testid="send-empty-toggle">
                  <div>
                    <p className="text-sm text-slate-700">Send even if no actionable items</p>
                    <p className="text-xs text-slate-400">You'll still receive a confirmation email with 0 items</p>
                  </div>
                  <Switch
                    checked={notifications.send_empty_digest}
                    onCheckedChange={v => setNotifications(n => ({ ...n, send_empty_digest: v }))}
                    data-testid="send-empty-switch"
                  />
                </div>

                <Separator />

                {/* Digest Content Toggles */}
                <div>
                  <button
                    onClick={() => setShowDigestContent(!showDigestContent)}
                    className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors w-full"
                    data-testid="digest-content-toggle"
                  >
                    <Bell className="w-4 h-4" />
                    Digest Content Preferences
                    {showDigestContent ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                  </button>

                  {showDigestContent && (
                    <div className="mt-3 space-y-1" data-testid="digest-content-list">
                      {DIGEST_SECTIONS.map(section => (
                        <div
                          key={section.key}
                          className="flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-slate-50 transition-colors"
                          data-testid={`digest-pref-${section.key}`}
                        >
                          <div>
                            <p className="text-sm text-slate-800">{section.label}</p>
                            <p className="text-xs text-slate-400">{section.desc}</p>
                          </div>
                          <Switch
                            checked={notifications.digest_preferences[section.key] !== false}
                            onCheckedChange={v => updateDigestPref(section.key, v)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Test & Preview Buttons */}
                <div className="flex flex-wrap gap-3 pt-1">
                  <Button
                    variant="outline"
                    onClick={handlePreviewDigest}
                    disabled={previewLoading}
                    data-testid="preview-digest-btn"
                  >
                    {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    Preview Digest
                  </Button>
                  <Button
                    onClick={handleSendTestDigest}
                    disabled={sendingTest}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="send-test-digest-btn"
                  >
                    {sendingTest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Test Digest
                  </Button>
                </div>

                {/* Preview Results */}
                {previewData && showDigestContent && (
                  <div className="mt-3 p-4 bg-slate-50 rounded-lg border text-sm" data-testid="digest-preview-results">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-slate-700">Digest Preview</p>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {previewData.total_items} items
                      </span>
                    </div>
                    {previewData.has_items ? (
                      <div className="space-y-1.5">
                        {Object.entries(previewData.sections).map(([key, count]) => (
                          <div key={key} className="flex justify-between text-xs">
                            <span className="text-slate-600">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                            <span className="font-mono text-slate-800">{count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-xs">No actionable items at this time.</p>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end pt-2">
              <Button onClick={handleSaveNotifications} disabled={saving} data-testid="save-notifications-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Preferences
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Timezone Info */}
        <div className="flex items-center gap-2 text-xs text-slate-400 pb-4">
          <Clock className="w-3.5 h-3.5" />
          <span>All times are in SAST (South Africa Standard Time, UTC+2)</span>
        </div>
      </div>
    </DashboardLayout>
  );
}
